import { Alert, Card, Empty, Menu, Select, Spin } from 'antd';
import Logo from 'components/Logo/Logo';
import { ProLockOverlay } from 'components/global/ProLock';
import { getTabComponent, resolveIcon } from 'providers/ClientTabs/clientTabsRegistry';
import { useClientTabs } from 'providers/ClientTabs/ClientTabsProvider';
import { useTenantData } from 'providers/Tenant/TenantDataProvider';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './AdminClientePage.scss';

// Branding genérico de la página de admin. El logo viene del componente Logo
// (que ya resuelve por tenant); slogan/descripción son agnósticos.
const TenantBranding = () => (
  <Card className="prolift-branding">
    <div className="branding-content">
      <div className="brand-logo">
        <Logo simple />
      </div>
      <div className="brand-message">
        <p className="brand-slogan">&quot;Elevando tu confianza, conectando tu futuro&quot;</p>
        <p className="brand-description">
          Especialistas en mantención y modernización de ascensores
        </p>
      </div>
    </div>
  </Card>
);

const AdminClientePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { clientes, loading: clientesLoading } = useTenantData();
  const { tabs, loading: tabsLoading, error: tabsError } = useClientTabs();
  const [current, setCurrent] = useState(null);
  const [selectedCliente, setSelectedCliente] = useState(null);

  // Resolver el clienteId inicial desde la URL o el primer cliente disponible.
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const clienteIdParam = queryParams.get('clienteId');

    if (clienteIdParam) {
      const cliente = clientes?.find((c) => c.id === Number(clienteIdParam));
      if (cliente) setSelectedCliente(cliente);
    } else if (clientes?.length && !selectedCliente) {
      const firstCliente = clientes[0];
      setSelectedCliente(firstCliente);
      const tab = queryParams.get('tab') || tabs?.[0]?.key || '';
      navigate(`?tab=${tab}&clienteId=${firstCliente.id}`);
    }
  }, [location.search, clientes, navigate, selectedCliente, tabs]);

  // Sincroniza el tab seleccionado con los tabs disponibles.
  useEffect(() => {
    if (!tabs || tabs.length === 0) {
      setCurrent(null);
      return;
    }
    const queryParams = new URLSearchParams(location.search);
    const requested = queryParams.get('tab');
    const valid = tabs.find((t) => t.key === requested);
    setCurrent(valid ? valid.key : tabs[0].key);
  }, [tabs, location.search]);

  const menuItems = useMemo(
    () =>
      tabs.map((t) => ({
        key: t.key,
        label: t.label,
        icon: resolveIcon(t.icon),
      })),
    [tabs]
  );

  const onClick = (e) => {
    setCurrent(e.key);
    const clienteId = selectedCliente?.id;
    navigate(`?tab=${e.key}${clienteId ? `&clienteId=${clienteId}` : ''}`);
  };

  const onChangeCliente = (value) => {
    const cliente = clientes?.find((c) => c.id === value);
    setSelectedCliente(cliente);
    navigate(`?tab=${current ?? ''}&clienteId=${value}`);
  };

  const ActiveComponent = current ? getTabComponent(current, 'administrador') : null;
  const currentTab = current ? tabs?.find((t) => t.key === current) : null;
  const isLocked = currentTab?.locked ?? false;

  return (
    <div className="admin-container">
      <div className="admin-content">
        <div className="space-y-6">
          {/* Header con selector de cliente */}
          <Card className="admin-header">
            <div className="header-content">
              <h1 className="header-title">Panel de Administración</h1>
              <div className="client-selector">
                <Select
                  className="w-full"
                  onChange={onChangeCliente}
                  loading={clientesLoading}
                  placeholder="Seleccione un cliente"
                  value={selectedCliente?.id}
                  size="large"
                >
                  {clientes?.map((cliente) => (
                    <Select.Option value={cliente.id} key={cliente.id}>
                      {cliente.name}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </div>
          </Card>

          <TenantBranding />

          {/* Navegación y contenido */}
          <Card className="admin-navigation">
            {tabsLoading ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <Spin size="large" />
              </div>
            ) : tabsError ? (
              <Alert
                type="error"
                showIcon
                message="No se pudieron cargar los tabs"
                description="Contactá al administrador si el problema persiste."
              />
            ) : !tabs || tabs.length === 0 ? (
              <Empty description="No hay tabs configurados para tu usuario en este tenant." />
            ) : (
              <>
                <Menu
                  onClick={onClick}
                  selectedKeys={current ? [current] : []}
                  mode="horizontal"
                  items={menuItems}
                  className="mb-4"
                />
                <div className="mt-4">
                  {!selectedCliente ? (
                    <Alert
                      message="Debe seleccionar un cliente para continuar"
                      type="warning"
                      showIcon
                      className="text-center"
                    />
                  ) : ActiveComponent ? (
                    <ProLockOverlay locked={isLocked}>
                      <ActiveComponent id={selectedCliente.id} />
                    </ProLockOverlay>
                  ) : null}
                </div>
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminClientePage;
